/**
 * Hook for exporting CRM objects to CSV
 */

import { useCallback, useState } from 'react';

import { getCSRFToken } from '@/lib/django';
import { fetchWithDefaults } from '@/lib/http/client';

export type EntityTypeName = 'ahoy_contact' | 'ahoy_company' | 'ahoy_deal';

interface UseCrmExportResult {
  exportContacts: () => Promise<void>;
  exportCompanies: () => Promise<void>;
  exportDeals: () => Promise<void>;
  exportingContacts: boolean;
  exportingCompanies: boolean;
  exportingDeals: boolean;
  error: string | null;
  clearError: () => void;
}

/**
 * Trigger a CSV file download from a blob response
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Extract filename from Content-Disposition header
 */
function getFilenameFromHeaders(headers: Headers, fallback: string): string {
  const contentDisposition = headers.get('Content-Disposition');
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
    if (match && match[1]) {
      return match[1];
    }
  }
  return fallback;
}

/**
 * Hook for exporting CRM objects to CSV files.
 *
 * Provides functions to export contacts, companies, and deals with
 * individual loading states for each export type.
 */
export function useCrmExport(orgId: number): UseCrmExportResult {
  const [exportingContacts, setExportingContacts] = useState(false);
  const [exportingCompanies, setExportingCompanies] = useState(false);
  const [exportingDeals, setExportingDeals] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportObjects = useCallback(
    async (
      entityType: EntityTypeName,
      setExporting: (value: boolean) => void,
      fallbackFilename: string
    ) => {
      try {
        setExporting(true);
        setError(null);

        const response = await fetchWithDefaults(
          `/api/orgs/${orgId}/crm-events/export/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken() || '',
            },
            body: JSON.stringify({
              entity_type_name: entityType,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error || errorData.detail || 'Export failed'
          );
        }

        const blob = await response.blob();
        const filename = getFilenameFromHeaders(
          response.headers,
          fallbackFilename
        );
        downloadBlob(blob, filename);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Export failed';
        setError(message);
        throw err;
      } finally {
        setExporting(false);
      }
    },
    [orgId]
  );

  const exportContacts = useCallback(async () => {
    await exportObjects('ahoy_contact', setExportingContacts, 'contacts.csv');
  }, [exportObjects]);

  const exportCompanies = useCallback(async () => {
    await exportObjects('ahoy_company', setExportingCompanies, 'companies.csv');
  }, [exportObjects]);

  const exportDeals = useCallback(async () => {
    await exportObjects('ahoy_deal', setExportingDeals, 'deals.csv');
  }, [exportObjects]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    exportContacts,
    exportCompanies,
    exportDeals,
    exportingContacts,
    exportingCompanies,
    exportingDeals,
    error,
    clearError,
  };
}
